import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { TrekRound } from '../../../interfaces/fraction-trek.interface';

interface Sparkle { x: number; y: number; r: number; delay: number; }

/**
 * Fraction Trek play surface — a 0–`maxTick` number line divided into
 * `den`-ths. A ghost marker sits at A (the first number); the child
 * drags the live marker (snapping to every 1/den) and a flag shows its
 * value as a mixed number, while a jump arc from A shows how far
 * they've moved. CHECK commits: landing on A ± B reveals the directed
 * jump + the equation; a wrong spot buzzes red and they re-drag.
 *
 * All positions are integer *d-ths* (`k`) so snapping/comparison is
 * exact. Presentation-only: CHECK is triggered by the shell via
 * @ViewChild; the outcome is reported with `solved` / `missed`.
 */
@Component({
  selector: 'app-fraction-trek-stage',
  templateUrl: './fraction-trek-stage.component.html',
  styleUrls: ['./fraction-trek-stage.component.scss']
})
export class FractionTrekStageComponent implements OnChanges {
  @Input() round: TrekRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 520;
  readonly X0 = 90;
  readonly X_END = 910;
  readonly LINE_Y = 320;

  /** Current marker position, in d-ths from 0. */
  k = 0;
  solvedState = false;
  wrongFlash = false;
  sparkles: Sparkle[] = [];

  private dragging = false;
  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  private reset(): void {
    this.clearTimers();
    this.solvedState = false;
    this.wrongFlash = false;
    this.dragging = false;
    this.sparkles = [];
    this.k = this.round ? this.round.startDths : 0;
  }

  // ── Geometry ───────────────────────────────────────────────────────

  private get maxTick(): number { return this.round?.maxTick ?? 5; }
  get den(): number { return this.round?.den ?? 1; }
  private get totalDths(): number { return this.maxTick * this.den; }
  get span(): number { return this.X_END - this.X0; }
  /** Pixels per 1/den. */
  private get tickPx(): number { return this.span / this.totalDths; }

  dthToX(dths: number): number { return this.X0 + dths * this.tickPx; }
  get markerX(): number { return this.dthToX(this.k); }
  get ghostX(): number { return this.dthToX(this.round?.startDths ?? 0); }
  get answerX(): number { return this.dthToX(this.round?.answerDths ?? 0); }

  get integerTicks(): number[] {
    return Array.from({ length: this.maxTick + 1 }, (_, i) => i);
  }
  /** Every 1/den mark (minor ticks). */
  get minorTicks(): number[] {
    return Array.from({ length: this.totalDths + 1 }, (_, i) => i);
  }
  isMajor(dths: number): boolean { return dths % this.den === 0; }

  // ── Labels ─────────────────────────────────────────────────────────

  private mixed(dths: number): { whole: number; num: number; den: number } {
    const den = this.den;
    return { whole: Math.floor(dths / den), num: ((dths % den) + den) % den, den };
  }

  private labelOf(dths: number): string {
    const m = this.mixed(Math.abs(dths));
    if (m.num === 0) return `${m.whole}`;
    if (m.whole === 0) return `${m.num}/${m.den}`;
    return `${m.whole} ${m.num}/${m.den}`;
  }

  get markerLabel(): string { return this.labelOf(this.k); }
  get startLabel(): string { return this.round ? this.labelOf(this.round.aDths) : ''; }

  /** Distance moved from the start, as a signed mixed-number label. */
  get movedDths(): number { return this.k - (this.round?.startDths ?? 0); }
  get movedLabel(): string {
    const mag = Math.abs(this.movedDths);
    if (mag === 0) return '';
    const sign = this.movedDths > 0 ? '+' : '−';
    return `${sign} ${this.labelOf(mag)}`;
  }

  /** Jump-arc path from the start marker to the current marker. */
  get jumpArc(): string {
    const x1 = this.ghostX;
    const x2 = this.markerX;
    if (Math.abs(x2 - x1) < 2) return '';
    const mid = (x1 + x2) / 2;
    const h = 46;
    return `M ${x1} ${this.LINE_Y - 4} Q ${mid} ${this.LINE_Y - h} ${x2} ${this.LINE_Y - 4}`;
  }
  get jumpMidX(): number { return (this.ghostX + this.markerX) / 2; }
  get opColor(): string { return this.round?.op === '-' ? '#ff8a80' : '#7ef0ff'; }

  /** Full equation for the success reveal, e.g. "2 2/5 − 1 1/5 = 1 1/5". */
  get equationLabel(): string {
    if (!this.round) return '';
    const op = this.round.op === '-' ? '−' : '+';
    return `${this.labelOf(this.round.aDths)} ${op} ${this.labelOf(this.round.bDths)} = ${this.labelOf(this.round.answerDths)}`;
  }

  // ── Drag ───────────────────────────────────────────────────────────

  onDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    this.dragging = true;
    this.wrongFlash = false;
    this.updateFromEvent(evt);
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onMove(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.updateFromEvent(evt);
  }

  onUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }

  private updateFromEvent(evt: PointerEvent): void {
    const x = this.toSvgX(evt);
    const dths = Math.round((x - this.X0) / this.tickPx);
    this.k = Math.max(0, Math.min(this.totalDths, dths));
  }

  // ── Check (called by the shell) ────────────────────────────────────

  get canCheck(): boolean {
    return this.interactive && !this.solvedState && this.k !== (this.round?.startDths ?? 0);
  }

  check(): void {
    if (!this.round || this.solvedState) return;
    if (this.k === this.round.answerDths) {
      this.solvedState = true;
      this.spawnSparkles(this.markerX);
      this.solved.emit();
    } else {
      this.wrongFlash = true;
      this.timers.push(setTimeout(() => { this.wrongFlash = false; }, 620));
      this.missed.emit();
    }
  }

  private spawnSparkles(cx: number): void {
    this.sparkles = Array.from({ length: 12 }, (_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const dist = 26 + (i % 3) * 18;
      return { x: cx + Math.cos(a) * dist, y: this.LINE_Y + Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
  }

  private toSvgX(evt: PointerEvent): number {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return 0;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    return pt.matrixTransform(ctm.inverse()).x;
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
